import { amber, blue, green, oceanGray, orange, red, violet } from "./palette";

/** Category identity shares primitive ramps with status, but carries no status meaning. */
export const dataVizRamps = { red, orange, amber, green, blue, violet, gray: oceanGray } as const;
export type DataVizKey = keyof typeof dataVizRamps;
export type DataVizTokens = Readonly<Record<DataVizKey, string>>;

/** Step names are the source of both token values and Storybook's forward/reverse links. */
export const dataVizPaletteSteps = {
  red: { light: "red70", dark: "red35" },
  orange: { light: "orange60", dark: "orange40" },
  amber: { light: "amber50", dark: "amber30" },
  green: { light: "green50", dark: "green40" },
  blue: { light: "blue90", dark: "blue90" },
  violet: { light: "violet130", dark: "violet40" },
  gray: { light: "gray130", dark: "gray130" },
} as const satisfies {
  [K in DataVizKey]: Record<"light" | "dark", keyof (typeof dataVizRamps)[K]>;
};

export const dataVizKeys = Object.keys(dataVizPaletteSteps) as DataVizKey[];

/** Tone name components accept for a categorical hue (`dataViz.green`). */
export type DataVizToneName = `dataViz.${DataVizKey}`;

const DATA_VIZ_TONE_PREFIX = "dataViz.";

export const isDataVizToneName = (value: string): value is DataVizToneName =>
  value.startsWith(DATA_VIZ_TONE_PREFIX) &&
  (dataVizKeys as readonly string[]).includes(value.slice(DATA_VIZ_TONE_PREFIX.length));

export const dataVizKeyOf = (tone: DataVizToneName): DataVizKey =>
  tone.slice(DATA_VIZ_TONE_PREFIX.length) as DataVizKey;

export const resolveDataVizTokens = (mode: "light" | "dark"): DataVizTokens =>
  Object.fromEntries(
    dataVizKeys.map((key) => [
      key,
      (dataVizRamps[key] as Record<string, string>)[dataVizPaletteSteps[key][mode]],
    ]),
  ) as DataVizTokens;
